import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL, getValidToken } from './utils/authUtils';
import AdminRouteGuard from './components/AdminRouteGuard';
import { ADMIN_SECTIONS, clearPermissionsCache } from './utils/adminPermissions';
import { ResponsiveTable, MobileCompanyCard, MobileSidebar } from './components/ResponsiveComponents';

const BACKEND_URL = API_URL;

const AdminCompanyManagement = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const token = getValidToken();
      
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.get(`${BACKEND_URL}/api/all-recruiters`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_inactive: showInactive }
      });
      setCompanies(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching companies:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Please log in as an admin.');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to load companies');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCompanyStatus = async (recruiterId, organizationName, currentStatus) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} "${organizationName}"?`)) {
      return;
    }

    try {
      const token = getValidToken();
      
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/toggle-recruiter-status`,
        recruiterId,
        { headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        } }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        // Update the company in the local state
        setCompanies(companies.map(company => 
          company.id === recruiterId 
            ? { ...company, is_active: !currentStatus }
            : company
        ));
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      console.error('Error toggling company status:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Please log in as an admin.');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Failed to update company status');
      }
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [showInactive]);

  // Filter and sort companies alphabetically by organization name
  const filteredCompanies = companies
    .filter(company =>
      company.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const orgA = (a.organization || 'No Organization').toLowerCase();
      const orgB = (b.organization || 'No Organization').toLowerCase();
      return orgA.localeCompare(orgB);
    });

  const activeCompanies = filteredCompanies.filter(c => c.is_active);
  const inactiveCompanies = filteredCompanies.filter(c => !c.is_active);

  return (
    <AdminRouteGuard requiredPermission={ADMIN_SECTIONS.COMPANY_MANAGEMENT}>
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        {/* Mobile Sidebar */}
        <MobileSidebar
          activeTab="company-management"
          onTabChange={(tabId) => {
            if (tabId === 'home') {
              navigate('/admin');
            } else if (tabId === 'students') {
              // Students button handled by grid menu
            } else if (tabId === 'recruiters') {
              navigate('/admin?tab=recruiters');
            } else if (tabId === 'profile') {
              // Logout functionality
              localStorage.removeItem('token');
              localStorage.removeItem('authToken');
              clearPermissionsCache();
              navigate('/');
            }
          }}
          navigate={navigate}
          isOpen={isMobileSidebarOpen}
          onToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col gap-4">
              {/* Header Info */}
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Company Management</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Activate or deactivate companies without deleting their data
                </p>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 sm:justify-end">
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  <span className="hidden sm:inline">Back to Admin Panel</span>
                  <span className="sm:hidden">Back to Admin</span>
                </button>
                <button
                  onClick={() => {
                    localStorage.removeItem('token');
                    localStorage.removeItem('authToken');
                    clearPermissionsCache();
                    navigate('/');
                    setTimeout(() => {
                      alert('Successfully logged out');
                    }, 100);
                  }}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Controls */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show inactive companies</span>
                </label>
              </div>
              
              <div className="flex-1 max-w-xs">
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{activeCompanies.length}</div>
                <div className="text-sm text-gray-500">Active Companies</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{inactiveCompanies.length}</div>
                <div className="text-sm text-gray-500">Inactive Companies</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{filteredCompanies.length}</div>
                <div className="text-sm text-gray-500">Total Shown</div>
              </div>
            </div>
          </div>

          {/* Company List */}
          <div className="px-6 py-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-sm text-gray-500">Loading companies...</p>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">No companies found</p>
              </div>
            ) : (
              <ResponsiveTable 
                data={filteredCompanies}
                renderMobileCard={(company, index) => (
                  <MobileCompanyCard
                    key={`${company.id}-${index}`}
                    company={company}
                    onToggleStatus={() => toggleCompanyStatus(company.id, company.organization, company.is_active)}
                  />
                )}
              >
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Recruiter
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Approved
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCompanies.map((company) => (
                        <tr key={company.id} className={!company.is_active ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {company.organization || 'No Organization'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{company.name}</div>
                            <div className="text-sm text-gray-500">{company.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              company.is_active 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {company.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              company.approved 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {company.approved ? 'Approved' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => toggleCompanyStatus(company.id, company.organization, company.is_active)}
                              className={`inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                company.is_active
                                  ? 'text-red-700 bg-red-100 hover:bg-red-200 focus:ring-red-500'
                                  : 'text-green-700 bg-green-100 hover:bg-green-200 focus:ring-green-500'
                              }`}
                            >
                              {company.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </ResponsiveTable>
            )}
          </div>
        </div>
        </div>
      </div>
    </AdminRouteGuard>
  );
};

export default AdminCompanyManagement;